/* ==========================================================================
 * ab-product-popup.js
 * --------------------------------------------------------------------------
 * "See more" for any product, anywhere, using the same popup as the cart
 * (ab-toast.js). Needs ab-toast.{js,css} on the page.
 *
 * Markup — one attribute on any button:
 *
 *   <button type="button" data-ab-product-popup="{{ product.url }}">See more</button>
 *
 * Optional, on an ancestor (e.g. an add-on tile) — adds an action button that
 * clicks that element, so it selects exactly as a tap on it would:
 *
 *   data-ab-popup-select            toggle: "Add to order" / "Remove from order"
 *   data-ab-popup-select="single"   pick-one (jars): "Choose this" / nothing once chosen
 *
 * If the tile selects from a child rather than from a tap anywhere on it
 * (older tiles: "+Add" adds, "It's in the bag!" removes), name those children
 * and they are clicked instead:
 *
 *   data-ab-popup-add=".link_hover" data-ab-popup-remove=".image_hover"
 *
 * Title, picture, intro and bullet points come from /products/<handle>.js,
 * fetched once per product and cached.
 * ========================================================================== */

(() => {
  'use strict';

  const TRIGGER = '[data-ab-product-popup]';
  const SELECT = '[data-ab-popup-select]';
  const SELECTED_CLASSES = ['active', 'is-added', 'is-selected'];

  const MAX_POINTS = 6;
  const INTRO_LIMIT = 260;
  const POINT_LIMIT = 180;
  const IMAGE_WIDTH = 640;

  const cache = new Map();

  const truncate = (text, limit) => (text.length > limit ? `${text.slice(0, limit - 1).trimEnd()}…` : text);

  const clean = (text) => (text || '').replace(/\s+/g, ' ').trim();

  /** "/products/rose?variant=1" -> "/products/rose.js" */
  const jsonUrl = (productUrl) => {
    const url = new URL(productUrl, window.location.origin);
    url.search = '';
    url.hash = '';
    url.pathname = `${url.pathname.replace(/\/$/, '').replace(/\.js$/, '')}.js`;
    return url.toString();
  };

  const sizedImage = (src) => {
    if (!src) {
      return '';
    }

    try {
      const url = new URL(src.startsWith('//') ? `https:${src}` : src, window.location.origin);
      url.searchParams.set('width', String(IMAGE_WIDTH));
      return url.toString();
    } catch {
      return src;
    }
  };

  /** Everything outside the lists is the intro; each list item is a point. */
  const splitDescription = (html) => {
    const body = new DOMParser().parseFromString(html || '', 'text/html').body;

    const points = [...body.querySelectorAll('li')]
      .map((item) => clean(item.textContent))
      .filter(Boolean)
      .slice(0, MAX_POINTS)
      .map((text) => truncate(text, POINT_LIMIT));

    body.querySelectorAll('ul, ol').forEach((list) => list.remove());

    const blocks = [...body.children].length ? [...body.children] : [body];
    const intro = blocks
      .map((block) => clean(block.textContent))
      .filter(Boolean)
      .join('\n');

    return { intro: truncate(intro, INTRO_LIMIT), points };
  };

  const details = (productUrl) => {
    const key = jsonUrl(productUrl);

    if (!cache.has(key)) {
      const request = fetch(key, { headers: { Accept: 'application/json' } })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          return response.json();
        })
        .then((product) => ({
          title: product.title || '',
          image: sizedImage(product.featured_image || product.images?.[0]),
          ...splitDescription(product.description)
        }))
        .catch((error) => {
          cache.delete(key); // let the next tap try again
          throw error;
        });

      cache.set(key, request);
    }

    return cache.get(key);
  };

  const isSelected = (element) =>
    element.matches('[aria-pressed="true"]') || SELECTED_CLASSES.some((name) => element.classList.contains(name));

  /** What the action button says, or '' for no action button. */
  const actionLabel = (target) => {
    if (!target) {
      return '';
    }

    const selected = isSelected(target);

    if (target.dataset.abPopupSelect === 'single') {
      return selected ? '' : 'Choose this';
    }

    return selected ? 'Remove from order' : 'Add to order';
  };

  const open = async (trigger) => {
    if (!window.AbToast || trigger.getAttribute('aria-busy') === 'true') {
      return;
    }

    const target = trigger.closest(SELECT);

    trigger.setAttribute('aria-busy', 'true');

    let product;

    try {
      product = await details(trigger.dataset.abProductPopup);
    } catch (error) {
      console.warn('[ab-product-popup] could not load', trigger.dataset.abProductPopup, error);
      return;
    } finally {
      trigger.removeAttribute('aria-busy');
    }

    const popup = {
      anchor: target || trigger,
      title: product.title,
      image: product.image,
      message: product.intro,
      points: product.points
    };

    const label = actionLabel(target);

    if (!label) {
      window.AbToast.notify({ ...popup, dismissText: 'Close' });
      return;
    }

    const act = await window.AbToast.confirm({ ...popup, cancelText: 'Close', confirmText: label });

    if (act && target.isConnected) {
      const selector = isSelected(target)
        ? target.dataset.abPopupRemove || target.dataset.abPopupAdd
        : target.dataset.abPopupAdd;
      const control = selector ? target.querySelector(selector) : null;
      (control || target).click();
    }
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest(TRIGGER);

    if (!trigger) {
      return;
    }

    event.preventDefault();
    open(trigger);
  });

  window.AbProductPopup = { open, details };
})();
